import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";

const WHATSAPP_NUM = "5582999393287";

const BUSCA_TYPES = ["Comercial", "Corporativo", "Industrial / Logístico", "Outro"];
const ANUNCIO_TYPES = ["Loja / Ponto Comercial", "Sala / Escritório", "Galpão / Industrial", "Outro"];

export const ContactModal = ({ open, onClose, type = "encontrar" }) => {
  const isEncontrar = type === "encontrar";
  const [form, setForm] = useState({
    name: "",
    phone: "",
    propertyType: "",
    area: "",
    city: "",
    message: "",
  });

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const intro = isEncontrar
      ? "Olá! Estou *procurando um imóvel* para locação."
      : "Olá! Gostaria de *anunciar um imóvel* para locação.";

    const lines = [
      intro,
      "",
      `*Nome:* ${form.name || "-"}`,
      `*Telefone:* ${form.phone || "-"}`,
      `*Tipo de imóvel:* ${form.propertyType || "-"}`,
      `*Área (m²):* ${form.area || "-"}`,
      `*Cidade / Região:* ${form.city || "-"}`,
      `*Mensagem:* ${form.message || "-"}`,
    ];
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${WHATSAPP_NUM}?text=${text}`, "_blank");
    onClose();
  };

  const types = isEncontrar ? BUSCA_TYPES : ANUNCIO_TYPES;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] bg-[#05091B]/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="modal-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 pointer-events-none"
          >
            <div
              className="pointer-events-auto relative bg-[#0B1A3A] border border-[#D4B366]/30 w-full max-w-xl max-h-[92vh] overflow-y-auto"
              data-testid={`contact-modal-${type}`}
            >
              <button
                onClick={onClose}
                className="absolute top-5 right-5 text-[#A2AAC2] hover:text-[#E8C97A] transition-colors z-10"
                data-testid="modal-close-btn"
                aria-label="Fechar"
              >
                <X size={22} strokeWidth={1.5} />
              </button>

              <div className="p-8 md:p-12">
                <div className="flex items-center gap-4 mb-5">
                  <div className="gold-divider" />
                  <span className="eyebrow">
                    {isEncontrar ? "Encontrar Imóvel" : "Anunciar Imóvel"}
                  </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl text-[#F5F1EA] leading-tight mb-3">
                  {isEncontrar ? (
                    <>Vamos encontrar o <span className="italic gold-text">espaço certo</span></>
                  ) : (
                    <>Vamos valorizar seu <span className="italic gold-text">imóvel</span></>
                  )}
                </h3>
                <p className="text-[#A2AAC2] text-sm leading-[1.75] font-light mb-8">
                  Preencha os dados abaixo e enviaremos sua solicitação diretamente pelo WhatsApp.
                  Nossa equipe retorna com as melhores opções para você.
                </p>

                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      required
                      placeholder="Seu nome *"
                      value={form.name}
                      onChange={handle("name")}
                      className="pc-input"
                      data-testid="form-name"
                    />
                    <input
                      type="tel"
                      required
                      placeholder="Telefone / WhatsApp *"
                      value={form.phone}
                      onChange={handle("phone")}
                      className="pc-input"
                      data-testid="form-phone"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <select
                      required
                      value={form.propertyType}
                      onChange={handle("propertyType")}
                      className="pc-input appearance-none cursor-pointer"
                      data-testid="form-property-type"
                    >
                      <option value="">Tipo de imóvel *</option>
                      {types.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Área aproximada (m²)"
                      value={form.area}
                      onChange={handle("area")}
                      className="pc-input"
                      data-testid="form-area"
                    />
                  </div>

                  <input
                    type="text"
                    placeholder={isEncontrar ? "Cidade / Região de interesse" : "Localização do imóvel"}
                    value={form.city}
                    onChange={handle("city")}
                    className="pc-input"
                    data-testid="form-city"
                  />

                  <textarea
                    rows={3}
                    placeholder={
                      isEncontrar
                        ? "Conte-nos mais sobre o imóvel que procura..."
                        : "Detalhes do imóvel (valor pretendido, características, etc.)"
                    }
                    value={form.message}
                    onChange={handle("message")}
                    className="pc-input resize-none"
                    data-testid="form-message"
                  />

                  <button
                    type="submit"
                    className="btn-gold w-full mt-2"
                    data-testid="form-submit"
                  >
                    <Send size={15} strokeWidth={1.5} />
                    Enviar via WhatsApp
                  </button>

                  <p className="text-[0.7rem] text-[#A2AAC2]/60 text-center tracking-wide">
                    Ao enviar, você será redirecionado para o WhatsApp com seus dados preenchidos.
                  </p>
                </form>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
